// Plan and usage (M7 plan D2, D3): the trial or subscription, what the plan includes and what is
// used. A banner on Home says when a trial is ending or the books are read-only.
import { useState } from 'react';
import { useOrgData } from './accountant/useOrgData.js';
import type { Api } from './api.js';
import { day, dollars, fromCents } from './format.js';

interface Plan {
  planName: string;
  priceMinor: string;
  status: 'trial' | 'active' | 'past_due' | 'read_only' | 'comped';
  trialEndsAt: string | null;
  readOnly: boolean;
  limits: Record<'people' | 'bankAccounts' | 'stripeAccounts' | 'aiReadsPerMonth', number> | null;
  usage: {
    people: number;
    bankAccounts: number;
    stripeAccounts: number;
    aiReadsThisMonth: number;
    storageBytes: string;
  };
}

function daysLeft(iso: string): number {
  return Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000));
}

export function PlanBanner({ api, orgId }: { api: Api; orgId: string }) {
  const { data } = useOrgData<Plan>(api, orgId, '/plan');
  if (!data) return null;
  if (data.readOnly)
    return (
      <p className="error">
        {data.status === 'trial'
          ? 'Your free trial has ended. Your books are read-only until you subscribe; you can still see and export everything.'
          : 'Your subscription has lapsed. Your books are read-only until the payment is updated.'}
      </p>
    );
  if (data.status === 'trial' && data.trialEndsAt && daysLeft(data.trialEndsAt) <= 7)
    return <p>Your free trial ends in {daysLeft(data.trialEndsAt)} days.</p>;
  return null;
}

/** Sends the owner to Stripe: Checkout to subscribe, the customer portal to manage. */
function BillingButton({
  api,
  orgId,
  status,
}: {
  api: Api;
  orgId: string;
  status: Plan['status'];
}) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (status === 'comped') return null;
  const subscribed = status === 'active' || status === 'past_due';
  async function go() {
    setBusy(true);
    setError('');
    try {
      const { url } = await api.send<{ url: string }>(
        orgId,
        'POST',
        subscribed ? '/billing/portal' : '/billing/checkout',
      );
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Stripe could not be reached.');
      setBusy(false);
    }
  }
  return (
    <div className="stack">
      <button type="button" disabled={busy} onClick={go}>
        {subscribed ? 'Manage billing (card, invoices, cancel)' : 'Subscribe'}
      </button>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

export function PlanPanel({ api, orgId }: { api: Api; orgId: string }) {
  const { data } = useOrgData<Plan>(api, orgId, '/plan');
  if (!data) return <p className="muted">Loading</p>;
  const rows: [string, number, number | undefined][] = [
    ['People', data.usage.people, data.limits?.people],
    ['Bank accounts', data.usage.bankAccounts, data.limits?.bankAccounts],
    ['Stripe accounts', data.usage.stripeAccounts, data.limits?.stripeAccounts],
    ['AI reads this month', data.usage.aiReadsThisMonth, data.limits?.aiReadsPerMonth],
  ];
  const status =
    data.status === 'comped'
      ? 'Free, no limits.'
      : data.status === 'trial'
        ? `Free trial until ${data.trialEndsAt ? day(data.trialEndsAt.slice(0, 10)) : ''}, then ${dollars(fromCents(data.priceMinor))} a month including GST.`
        : data.status === 'active'
          ? `${dollars(fromCents(data.priceMinor))} a month including GST.`
          : 'Payment needed.';
  return (
    <section className="stack bank-card">
      <h2>Plan: {data.planName}</h2>
      <p>{status}</p>
      {new URLSearchParams(window.location.search).has('subscribed') && data.status === 'trial' && (
        <p className="muted">
          Thank you. Stripe is confirming the subscription; this shows within a minute.
        </p>
      )}
      <BillingButton api={api} orgId={orgId} status={data.status} />
      <table className="table">
        <tbody>
          {rows.map(([name, used, limit]) => (
            <tr key={name} className={limit !== undefined && used >= limit ? 'error' : undefined}>
              <td>{name}</td>
              <td className="num">{limit === undefined ? used : `${used} of ${limit}`}</td>
            </tr>
          ))}
          <tr>
            <td>Files stored</td>
            <td className="num">{(Number(data.usage.storageBytes) / 1_048_576).toFixed(1)} MB</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}
